import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { FieldErrors, FieldValues, Path, PathValue, useForm, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { Button, Card, ScrollView, Sheet, Text, XStack, YStack } from "tamagui";
import { z } from "zod";

// Components & Utils
import RadiusBoxLabel from "@components/layout/RadiusBoxLabel";
import { colors } from "@styles/colors";
import { combinedErrorMessage } from "@utils/func/textTool";
import { useAddressStore } from "@utils/store/utils/useAddressStore";
import { RiAddLine, RiCloseFill, RiMapPinLine, RiPencilLine } from "react-icons/ri";
import AddressAutoComplete from "./AddressAutoComplete";
import AddressForm from "./AddressForm";
import { getAddressSelectionCoordinates, normalizeAddressSelectionData } from "./func/addressSelectionTool";
import { cleanKeyword } from "./func/addressTextTool";
import { combinedAddress } from "./func/addressTool";

const formSchema = z.object({
  addressId: z.number().optional().nullable(),
  city: z.string().min(1, "請輸入城市"),
  area: z.string().min(1, "請輸入區域"),
  road: z.string().min(1, "請輸入路名"),
  streetNumber: z.string().min(1, "請輸入門牌號"),
  floor: z.string().optional(),
  room: z.string().optional(),
  otherAddress: z.string().optional(),
  latitude: z.number().optional().nullable(),
  longitude: z.number().optional().nullable(),
});

interface AddressProps<T extends FieldValues> {
  setValue: UseFormSetValue<T>;
  watch: UseFormWatch<T>;
  parentError?: FieldErrors<T>[keyof T];
}

function Address<T extends FieldValues>({ setValue: setParentValue, watch: parentWatch, parentError }: AddressProps<T>) {
  const { setAutoCompleteAddress, autoCompleteAddress, addressViewMode, setAddressViewMode, setMapPickerDisabled } = useAddressStore();

  const [open, setOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null); //-- 正在編輯哪一筆
  const parentAddressList: any[] = parentWatch("isAddress" as Path<T>) || [];

  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      addressId: null,
      city: "",
      area: "",
      road: "",
      streetNumber: "",
      floor: "",
      room: "",
      otherAddress: "",
      latitude: null,
      longitude: null,
    },
  });
  type FormData = z.infer<typeof formSchema>;

  useEffect(() => {
    if (autoCompleteAddress) {
      const normalizedAddress = normalizeAddressSelectionData(autoCompleteAddress);
      const coords = getAddressSelectionCoordinates(normalizedAddress);
      methods.setValue("city", normalizedAddress?.administrative_area_level_1 || "");
      methods.setValue("area", normalizedAddress?.administrative_area_level_2 || "");
      methods.setValue("road", normalizedAddress?.route || "");
      methods.setValue("streetNumber", cleanKeyword(normalizedAddress?.street_number || "", "號"));
      methods.setValue("latitude", coords?.lat ?? null);
      methods.setValue("longitude", coords?.lng ?? null);
    }
  }, [autoCompleteAddress]);

  //-- 新增地址按鈕
  const handleAddMode = () => {
    setEditingIndex(null);
    methods.reset({
      addressId: null,
      city: "",
      area: "",
      road: "",
      streetNumber: "",
      floor: "",
      room: "",
      otherAddress: "",
      latitude: null,
      longitude: null,
    });
    setAutoCompleteAddress(null);
    setAddressViewMode("autoComplete");
    setOpen(true);
  };

  //-- 編輯地址按鈕
  const handleEditMode = (index: number, data: FormData) => {
    setEditingIndex(index);
    methods.reset(data); // 將舊資料填入內部表單
    setAddressViewMode("addressForm");
    setOpen(true);
    methods.setValue("addressId", data?.addressId || null);
    methods.setValue("city", data.city || "");
    methods.setValue("area", data.area || "");
    methods.setValue("road", data.road || "");
    methods.setValue("streetNumber", data.streetNumber);
    methods.setValue("latitude", data?.latitude || null);
    methods.setValue("longitude", data?.longitude || null);
    methods.setValue("otherAddress", data?.otherAddress || "");
  };

  // 刪除地址
  const handleDelete = (index: number) => {
    const newList = [...parentAddressList];
    newList.splice(index, 1);
    setParentValue("isAddress" as Path<T>, newList as PathValue<T, Path<T>>, {
      shouldValidate: true,
    });
  };

  const onSubmit = (data: FormData) => {
    let newList = [...parentAddressList];

    if (editingIndex !== null) {
      //-- 修改現有地址
      newList[editingIndex] = data;
    } else {
      //-- 新增一筆地址
      newList.push(data);
    }

    setParentValue("isAddress" as Path<T>, newList as PathValue<T, Path<T>>, {
      shouldValidate: true,
    });
    setOpen(false);
    setAutoCompleteAddress(null);
    setMapPickerDisabled(false);
  };

  return (
    <div>
      {/* 顯示儲存的地址 */}
      <YStack gap="$2" mb="$3">
        {parentAddressList.map((item, index) => (
          <>
            <XStack key={index} alignItems="center" gap="$2">
              <Card
                f={1}
                p="$3"
                borderWidth={1}
                borderColor={
                  Array.isArray(parentError) && parentError[index] && combinedErrorMessage(parentError[index]) ? "$red10" : "$borderColor"
                }>
                <RadiusBoxLabel label={combinedAddress(item)} desc={item.otherAddress || ""} icon={<RiMapPinLine color={colors.primary} />} />
                <XStack justifyContent="flex-end">
                  <XStack width="90%" alignItems="center" justifyContent="space-between" gap="0">
                    {/* 編輯按鈕 */}
                    <Button chromeless p={0} fontWeight="bold" onPress={() => handleEditMode(index, item)} color={colors.primary} className="flex">
                      <RiPencilLine size={14} />
                      編輯
                    </Button>
                    {/* 刪除按鈕 */}
                    <Button chromeless p={0} fontWeight="bold" onPress={() => handleDelete(index)} color={colors.darkRed}>
                      刪除
                    </Button>
                  </XStack>
                </XStack>
              </Card>
            </XStack>
            {Array.isArray(parentError) && parentError[index] && (
              <Text color="$red10" fontSize={12}>
                {combinedErrorMessage(parentError[index])}
              </Text>
            )}
          </>
        ))}
      </YStack>

      <Button
        chromeless
        color={colors.primary}
        width="100%"
        size="$3"
        mt="$2"
        borderRadius="$4"
        borderWidth={1.5}
        borderStyle="dashed"
        borderColor={colors.primary}
        backgroundColor={colors.primaryActive}
        pressStyle={{ backgroundColor: colors.primaryActive, borderStyle: "dashed", borderColor: colors.primary, borderWidth: 1.5 }}
        hoverStyle={{ backgroundColor: colors.primaryActive, borderStyle: "dashed", borderColor: colors.primary, borderWidth: 1.5 }}
        onPress={handleAddMode}
        icon={<RiAddLine size={16} />}>
        <Text fontSize={14} color={colors.primary}>
          新增
        </Text>
      </Button>
      <Sheet disableDrag modal open={open} onOpenChange={setOpen} snapPoints={[95]} dismissOnSnapToBottom={false} dismissOnOverlayPress={false} animation="medium">
        <Sheet.Overlay animation="lazy" enterStyle={{ opacity: 0 }} exitStyle={{ opacity: 0 }} backgroundColor="rgba(0,0,0,0.4)" />
        <Sheet.Handle />
        <Sheet.Frame bg="#ffffff" borderTopLeftRadius={24} borderTopRightRadius={24}>
          <XStack alignItems="center" justifyContent="space-between" padding="$5">
            <YStack>
              <Text fontWeight="bold">新增配送地址</Text>
              <Text fontSize={14} color={colors.gray}>
                搜尋地址並調整精確位置
              </Text>
            </YStack>
            <Button
              p={4}
              chromeless
              onPress={() => setOpen(false)}
              backgroundColor={colors.primaryActive}
              pressStyle={{ backgroundColor: colors.primaryActive, borderColor: colors.primaryActive, borderWidth: 1 }}
              hoverStyle={{ backgroundColor: colors.primaryActive, borderColor: colors.primaryActive, borderWidth: 1 }}>
              <RiCloseFill size={22} />
            </Button>
          </XStack>
          <ScrollView showsVerticalScrollIndicator={false} background={colors.bg}>
            <YStack className="gap-3">
              {open && (
                <div className="address">
                  {addressViewMode === "autoComplete" ? <AddressAutoComplete /> : <AddressForm methods={methods} onSubmit={onSubmit} />}
                </div>
              )}
            </YStack>
          </ScrollView>
        </Sheet.Frame>
      </Sheet>
    </div>
  );
}

export default Address;
